import React, { FC } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  Box,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ContractorDto } from '@vatos-pas/common';

import { SnackbarContainer } from 'components/standalone';

import { ContractorFormValues } from '../containers';
import { RESOURCE_TYPE_CONFIG } from '../resource-types';

type ToggleFieldName =
  | 'hangerAvailable'
  | 'finisherAvailable'
  | 'sprayerAvailable'
  | 'trimAvailable'
  | 'paintAvailable'
  | 'bumperAvailable'
  | 'specialistAvailable';

type CapacityFieldName =
  | 'hangerCapacity'
  | 'finisherCapacity'
  | 'sprayerCapacity'
  | 'trimCapacity'
  | 'paintCapacity'
  | 'bumperCapacity';

interface ResourceTypeRowConfig {
  label: string;
  toggleField: ToggleFieldName;
  capacityField?: CapacityFieldName;
  unit?: 'Boards' | 'Sq Ft';
  cyKey: string;
}

const RESOURCE_ROWS: ResourceTypeRowConfig[] = [
  {
    label: 'Hanger',
    toggleField: 'hangerAvailable',
    capacityField: 'hangerCapacity',
    unit: 'Boards',
    cyKey: 'hanger',
  },
  {
    label: 'Finisher',
    toggleField: 'finisherAvailable',
    capacityField: 'finisherCapacity',
    unit: 'Boards',
    cyKey: 'finisher',
  },
  {
    label: 'Sprayer',
    toggleField: 'sprayerAvailable',
    capacityField: 'sprayerCapacity',
    unit: 'Boards',
    cyKey: 'sprayer',
  },
  {
    label: 'Trim',
    toggleField: 'trimAvailable',
    capacityField: 'trimCapacity',
    unit: 'Sq Ft',
    cyKey: 'trim',
  },
  {
    label: 'Paint',
    toggleField: 'paintAvailable',
    capacityField: 'paintCapacity',
    unit: 'Sq Ft',
    cyKey: 'paint',
  },
  {
    label: 'Bumper',
    toggleField: 'bumperAvailable',
    capacityField: 'bumperCapacity',
    unit: 'Boards',
    cyKey: 'bumper',
  },
  {
    label: 'Specialist',
    toggleField: 'specialistAvailable',
    cyKey: 'specialist',
  },
];

// A toggle that cannot be switched on until another field holds a value —
// Bumper requires a Bumper Payment Type. Read off the canonical config so the
// rule and its copy live in resource-types.ts only.
type PaymentTypeGate = {
  field: keyof ContractorFormValues;
  message: string;
};

const getPaymentTypeGate = (
  toggleField: ToggleFieldName,
): PaymentTypeGate | undefined => {
  const config = RESOURCE_TYPE_CONFIG.find(
    item => item.sourceToggle === toggleField,
  );

  if (!config?.paymentTypeSource || !config.paymentTypeMessage)
    return undefined;

  return {
    field: config.paymentTypeSource as keyof ContractorFormValues,
    message: config.paymentTypeMessage,
  };
};

export interface ResourceTypeTableEditableProps {
  readOnly?: false;
  control: Control<ContractorFormValues>;
  watch: UseFormWatch<ContractorFormValues>;
  setValue: UseFormSetValue<ContractorFormValues>;
  trigger: UseFormTrigger<ContractorFormValues>;
  errors: FieldErrors<ContractorFormValues>;
  isSubmitted: boolean;
  record?: Partial<ContractorDto> | null;
}

export interface ResourceTypeTableReadOnlyProps {
  readOnly: true;
  record?: Partial<ContractorDto> | null;
}

export type ResourceTypeTableProps =
  | ResourceTypeTableEditableProps
  | ResourceTypeTableReadOnlyProps;

const blockDecimalKey = (event: React.KeyboardEvent): void => {
  if (event.key === '.' || event.key === ',') {
    event.preventDefault();
  }
};

const blockDecimalPaste = (
  event: React.ClipboardEvent<HTMLInputElement>,
): void => {
  const text = event.clipboardData.getData('text');
  if (/[.,]/.test(text)) {
    event.preventDefault();
  }
};

interface EditableRowProps extends ResourceTypeRowConfig {
  control: Control<ContractorFormValues>;
  watch: UseFormWatch<ContractorFormValues>;
  setValue: UseFormSetValue<ContractorFormValues>;
  trigger: UseFormTrigger<ContractorFormValues>;
  errors: FieldErrors<ContractorFormValues>;
  isSubmitted: boolean;
}

const EditableRow: FC<EditableRowProps> = ({
  label,
  toggleField,
  capacityField,
  unit,
  cyKey,
  control,
  watch,
  setValue,
  trigger,
  errors,
  isSubmitted,
}) => {
  const { notify } = SnackbarContainer.useContainer();
  const isEnabled = Boolean(watch(toggleField));
  const capacityError = capacityField ? errors[capacityField] : undefined;
  const paymentTypeGate = getPaymentTypeGate(toggleField);
  const paymentTypeMissing = !!paymentTypeGate && !watch(paymentTypeGate.field);

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <Box data-cy={`contractorForm-${cyKey}AvailableSwitch`}>
          <Controller
            name={toggleField}
            control={control}
            render={({ field }) => (
              <Switch
                checked={!!field.value}
                onBlur={field.onBlur}
                onChange={(_event, checked) => {
                  // Turning the toggle on is blocked while its required payment
                  // type is blank/N/A. Skipping field.onChange leaves the
                  // controlled Switch off, so there is nothing to revert.
                  if (checked && paymentTypeGate && paymentTypeMissing) {
                    notify(paymentTypeGate.message, 'error');
                    return;
                  }
                  field.onChange(checked);
                  if (!checked && capacityField) {
                    setValue(capacityField, 0, { shouldDirty: true });
                  }
                  if (capacityField && isSubmitted) {
                    trigger(capacityField);
                  }
                }}
                color="primary"
                inputRef={field.ref}
                inputProps={{ 'aria-label': `${label} available` }}
              />
            )}
          />
        </Box>
      </TableCell>
      <TableCell>
        {capacityField && (
          <Box data-cy={`contractorForm-${cyKey}CapacityInput`}>
            <Controller
              name={capacityField}
              control={control}
              render={({ field }) => (
                <TextField
                  label={unit}
                  type="number"
                  disabled={!isEnabled}
                  value={field.value ?? ''}
                  onBlur={field.onBlur}
                  onChange={event => {
                    const raw = event.target.value;
                    field.onChange(raw === '' ? null : Number(raw));
                  }}
                  inputRef={field.ref}
                  error={!!capacityError}
                  helperText={capacityError?.message}
                  onKeyDown={blockDecimalKey}
                  onPaste={blockDecimalPaste}
                  inputProps={{ step: 1 }}
                  margin="dense"
                  variant="filled"
                />
              )}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

interface ReadOnlyRowProps extends ResourceTypeRowConfig {
  record?: Partial<ContractorDto> | null;
}

const ReadOnlyRow: FC<ReadOnlyRowProps> = ({
  label,
  toggleField,
  capacityField,
  unit,
  record,
}) => {
  const isEnabled = !!record?.[toggleField];
  const capacityValue = capacityField ? record?.[capacityField] : null;
  const hasCapacity = isEnabled && capacityValue != null;

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <Switch checked={isEnabled} disabled color="primary" />
      </TableCell>
      <TableCell>
        {capacityField && hasCapacity && (
          <span>
            {capacityValue}{' '}
            {unit === 'Boards' && capacityValue === 1 ? 'Board' : unit}
          </span>
        )}
        {capacityField && !hasCapacity && (
          <span style={{ color: 'rgba(0,0,0,0.38)' }}>—</span>
        )}
      </TableCell>
    </TableRow>
  );
};

export const ResourceTypeTable: FC<ResourceTypeTableProps> = props => {
  const classes = useStyles();

  return (
    <Paper variant="outlined" className={classes.paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={`${classes.nameCell} ${classes.headerCell}`}>
              Resource Type
            </TableCell>
            <TableCell className={classes.toggleCell} />
            <TableCell
              className={`${classes.capacityCell} ${classes.headerCell}`}
            >
              Capacity
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {RESOURCE_ROWS.map(row =>
            props.readOnly ? (
              <ReadOnlyRow
                key={row.toggleField}
                {...row}
                record={props.record}
              />
            ) : (
              <EditableRow
                key={row.toggleField}
                {...row}
                control={props.control}
                watch={props.watch}
                setValue={props.setValue}
                trigger={props.trigger}
                errors={props.errors}
                isSubmitted={props.isSubmitted}
              />
            ),
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};

const useStyles = makeStyles(theme => ({
  paper: { maxWidth: 560, margin: theme.spacing(0, 1.875), overflow: 'hidden' },
  headerCell: { padding: theme.spacing(3, 2) },
  nameCell: { width: 140, fontWeight: 700 },
  toggleCell: { width: 80 },
  capacityCell: { minWidth: 180, fontWeight: 700 },
}));

export default ResourceTypeTable;
